import { DraftWaypointStatus, WaypointStatusName } from '@caresend/types';
import { getPluralCount, hasStatus, initTimeZoneDate, getStreetNameByLocation, formatTimeZoneDate } from '@caresend/utils';
import dayjs from 'dayjs';
import { computed } from 'vue';
import { ButtonStyle } from '../../../components/buttons/ButtonComponent/model.js';
import { getRouter } from '../../helpers/initRouter.js';
import { draftWaypointDetailsRoute, rescheduleVisitRoute, cancelVisitRoute } from '../locations.js';
import { schedulingFlowRoute } from '../schedulingFlow/locations.js';
import { getDraftWaypointDetailsBackLocation } from './helpers.js';
import { VisitsPageRouteName } from './model.js';
import { isPatientUser } from '../../../store/helpers.js';
import { getStore } from '../../../store/helpers/initStore.js';
import { useDraftWaypointsStore } from '../../../store/pinia/draftWaypoints/index.js';
import { isCancelVisitDryRun } from '../../../store/pinia/draftWaypoints/helpers.js';

// Paths below are not full paths - they are based on the parent path:
// /:partnerName?/edit-order/:draftOrderID?
const routesVisitsPage = [
    {
        path: '/:partnerName?/office/visits/',
        name: VisitsPageRouteName.VISITS_PAGE,
        props: true,
        component: () => import('../../../components/specific/VisitsPage/VisitsPage.vue.js'),
        meta: {
            authRequired: true,
        },
    },
    {
        path: '/:partnerName?/visit/:draftWaypointID/',
        name: VisitsPageRouteName.DRAFT_WAYPOINT_DETAILS,
        props: true,
        component: () => import('../../../components/specific/DraftWaypointDetails/DraftWaypointDetails.vue.js'),
        meta: {
            allowIncomplete: true,
            authRequired: true,
            /** We should move this business logic to helpers */
            heading: ({ route }) => computed(() => {
                var _a, _b;
                const draftWaypointStore = useDraftWaypointsStore();
                const { getters } = draftWaypointStore;
                const { draftWaypointID } = route.params;
                if (!draftWaypointID)
                    throw new Error('Draft waypoint ID is missing when loading visit details page');
                const draftWaypoint = getters.getDenormalizedDraftWaypoint(draftWaypointID);
                const backLocation = getDraftWaypointDetailsBackLocation(draftWaypoint);
                if (!draftWaypoint)
                    return { title: '', backLocation };
                const patientNumber = getters.getNumberOfPatientsOnVisit(draftWaypointID);
                let title = patientNumber === 0
                    ? 'Visit'
                    : `Visit for ${getPluralCount(patientNumber, 'patient', true)}`;
                if (!hasStatus(draftWaypoint.status, [
                    DraftWaypointStatus.PENDING,
                    DraftWaypointStatus.REJECTED,
                ])) {
                    const facilityDetails = draftWaypointStore.getters
                        .getFacilityDetailsOnDraftWaypoint(draftWaypoint.id);
                    if (facilityDetails && facilityDetails.name) {
                        title = `Visit to ${facilityDetails.name}`;
                    }
                    else {
                        const { canceled, date } = draftWaypoint;
                        const visitCanceled = hasStatus(draftWaypoint, DraftWaypointStatus.CANCELED);
                        // The date used for the title is the finalized date, unless the visit was
                        // canceled before it was finalized.
                        const finalizedDate = date;
                        const canceledDate = (canceled === null || canceled === void 0 ? void 0 : canceled.timestamp) ? initTimeZoneDate((_a = draftWaypoint.location) === null || _a === void 0 ? void 0 : _a.timeZone, canceled === null || canceled === void 0 ? void 0 : canceled.timestamp) : undefined;
                        const location = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.location;
                        const locationDisplayed = (location === null || location === void 0 ? void 0 : location.addressComponents)
                            ? ` at ${getStreetNameByLocation(location)}`
                            : '';
                        const formattedCanceledDate = canceledDate
                            ? `Visit canceled on ${formatTimeZoneDate(canceledDate, 'MMMM D')}`
                            : 'Visit canceled';
                        const formattedVisitDate = finalizedDate
                            ? `Visit${locationDisplayed} on ${formatTimeZoneDate(finalizedDate, 'MMMM D')}`
                            : 'Visit on TBA';
                        title = visitCanceled && !finalizedDate
                            ? formattedCanceledDate
                            : formattedVisitDate;
                    }
                }
                const router = getRouter();
                const handleReschedule = () => {
                    router.push(rescheduleVisitRoute({
                        params: {
                            draftWaypointID: draftWaypoint.id,
                        },
                    }));
                };
                const handleCancel = () => {
                    router.push(cancelVisitRoute({
                        params: {
                            draftWaypointID: draftWaypoint.id,
                        },
                    }));
                };
                const currentTime = Date.now();
                // Calculate 24-hour check once to share between both computed properties
                const finalizedDate = (_b = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.date) === null || _b === void 0 ? void 0 : _b.timestamp;
                // Check if this is a lab visit (has drawPlaceID) vs home visit
                const isLabVisit = !!(draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.drawPlaceID);
                // Apply different time restrictions based on visit type
                const isMoreThan24HoursBeforeFinalized = finalizedDate
                    ? dayjs(currentTime).isBefore(dayjs(finalizedDate).subtract(24, 'hours'))
                    : false;
                const isBeforeScheduledTime = finalizedDate
                    ? dayjs(currentTime).isBefore(dayjs(finalizedDate))
                    : false;
                // For CancerGuard visits, determine if modification is allowed based on visit type
                const checkCanModifyCancerGuardVisit = () => {
                    if (!draftWaypointID) {
                        throw new Error('Draft waypoint ID is missing when checking if CancerGuard visit can be modified');
                    }
                    if (!finalizedDate)
                        return false;
                    if (!getters.isCancerguardVisit(draftWaypointID))
                        return false;
                    if (isLabVisit)
                        return isBeforeScheduledTime;
                    return isMoreThan24HoursBeforeFinalized;
                };
                const canModifyCancerGuardVisit = checkCanModifyCancerGuardVisit();
                const showRescheduleButtonInHeader = computed(() => {
                    if (!draftWaypointID)
                        return false;
                    const baseReschedulePossible = getters.getIsReschedulingPossible(draftWaypointID);
                    if (!baseReschedulePossible)
                        return false;
                    if (!draftWaypoint)
                        return false;
                    if (hasStatus(draftWaypoint.status, DraftWaypointStatus.CANCELED)) {
                        return false;
                    }
                    // For patients on a cancer guard visit, require 24+ hours before finalized date
                    if (isPatientUser()) {
                        if (canModifyCancerGuardVisit) {
                            return true;
                        }
                        return false;
                    }
                    if (hasStatus(draftWaypoint.status, DraftWaypointStatus.IN_ITINERARY)) {
                        const { patientWaypointInfo } = draftWaypoint;
                        if (patientWaypointInfo === null || patientWaypointInfo === void 0 ? void 0 : patientWaypointInfo.status) {
                            if (hasStatus(patientWaypointInfo.status, [
                                WaypointStatusName.COMPLETE,
                                WaypointStatusName.AT_WAYPOINT,
                            ])) {
                                return false;
                            }
                        }
                    }
                    const isFinalizedOrInItinerary = hasStatus(draftWaypoint.status, [
                        DraftWaypointStatus.FINALIZED,
                        DraftWaypointStatus.IN_ITINERARY,
                    ]);
                    return isFinalizedOrInItinerary;
                });
                /**
                 * Disabled until further notice because we are using the fixed mobile
                 * footer for these options
                 */
                // const displayKebab = computed<boolean>(() => false);
                /**
                 * At the moment we intend to show the cancel button in the header
                 * and footer in all cases (except when the visit is canceled)
                 */
                const shouldShowCancelButton = computed(() => {
                    // Don't show if already canceled
                    if (hasStatus(draftWaypoint.status, DraftWaypointStatus.CANCELED)) {
                        return false;
                    }
                    // For patients: only apply restrictions if visit has CancerGuard products
                    if (isPatientUser()) {
                        const hasCancerGuardProducts = getters.isCancerguardVisit(draftWaypointID);
                        if (hasCancerGuardProducts && canModifyCancerGuardVisit) {
                            return true;
                        }
                        return false;
                    }
                    // For office users: check waypoint status when in itinerary
                    if (!isPatientUser() && hasStatus(draftWaypoint.status, DraftWaypointStatus.IN_ITINERARY)) {
                        const { patientWaypointInfo } = draftWaypoint;
                        if (patientWaypointInfo === null || patientWaypointInfo === void 0 ? void 0 : patientWaypointInfo.status) {
                            if (hasStatus(patientWaypointInfo.status, [
                                WaypointStatusName.COMPLETE,
                                WaypointStatusName.AT_WAYPOINT,
                            ])) {
                                return false;
                            }
                        }
                    }
                    return true;
                });
                return {
                    overflowVisible: true,
                    title,
                    backLocation,
                    ...(shouldShowCancelButton.value ? {
                        secondaryComponent: () => import('../../../components/specific/DraftWaypointDetails/CancelButton.vue.js'),
                        secondaryComponentProps: {
                            onClick: handleCancel,
                            iconLeft: 'trash',
                        },
                    } : {}),
                    ...(showRescheduleButtonInHeader.value ? {
                        confirmText: 'Reschedule',
                        confirmEvent: handleReschedule,
                        confirmButtonStyle: ButtonStyle.OUTLINE,
                        confirmIconLeft: 'calendar',
                    } : {}),
                    // Disabled until further notice because we are using the fixed mobile
                    // footer for these options
                    // ...(displayKebab.value ? {
                    //   tertiaryComponent: () => import('@components/specific/DraftWaypointDetails/VisitDetailsKebabButton.vue'),
                    //   tertiaryComponentProps: { denormalizedDraftWaypoint: draftWaypoint },
                    // } : {}),
                    mobileFooter: shouldShowCancelButton.value || showRescheduleButtonInHeader.value,
                };
                // Above type-cast is for vite/vue-tsc
            }),
            loadingMessage: () => 'Loading visit…',
            whiteBackground: true,
        },
    },
    {
        path: '/:partnerName?/reschedule/:draftWaypointID/',
        name: VisitsPageRouteName.RESCHEDULE_VISIT,
        props: true,
        component: () => import('../../../components/specific/RescheduleVisit/RescheduleVisit.vue.js'),
        meta: {
            allowIncomplete: true,
            authRequired: true,
            heading: ({ route }) => computed(() => {
                const draftWaypointStore = useDraftWaypointsStore();
                const { getters } = draftWaypointStore;
                const { draftWaypointID } = route.params;
                const backLocation = draftWaypointID
                    ? draftWaypointDetailsRoute({ params: { draftWaypointID } })
                    : undefined;
                const isReschedulePossible = computed(() => draftWaypointID
                    ? getters.getIsReschedulingPossible(draftWaypointID)
                    : false);
                const rescheduleRoute = computed(() => isReschedulePossible.value && draftWaypointID
                    ? schedulingFlowRoute(draftWaypointID, undefined, true)
                    : undefined);
                return {
                    backLocation,
                    backButtonText: 'Back to visit details',
                    confirmDisabled: computed(() => !isReschedulePossible.value),
                    confirmText: 'Reschedule',
                    ...(isReschedulePossible.value && {
                        confirmTo: rescheduleRoute.value,
                    }),
                    leftAlignTitle: true,
                    mobileFooter: true,
                };
            }),
            whiteBackground: true,
        },
    },
    {
        path: '/:partnerName?/cancel/:draftWaypointID/',
        name: VisitsPageRouteName.CANCEL_VISIT,
        props: true,
        component: () => import('../../../components/specific/CancelVisit/CancelVisit.vue.js'),
        meta: {
            allowIncomplete: true,
            authRequired: true,
            heading: ({ route }) => computed(() => {
                const { draftWaypointID } = route.params;
                const store = getStore();
                const draftWaypoint = draftWaypointID
                    ? store.state.waypoint.draftWaypoints[draftWaypointID]
                    : undefined;
                const isDryRun = isCancelVisitDryRun(draftWaypoint);
                const title = isDryRun ? 'Cancel visit (dry run)' : 'Cancel visit';
                const backLocation = draftWaypointID
                    ? draftWaypointDetailsRoute({ params: { draftWaypointID } })
                    : undefined;
                return {
                    title,
                    backLocation,
                };
            }),
            whiteBackground: true,
        },
    },
];
const getRoutesVisitsPage = () => routesVisitsPage;

export { getRoutesVisitsPage };
